import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxPaginationModule } from 'ngx-pagination';
import { AngularImageViewerModule, CustomImageEvent } from '@clarivate/angular-image-viewer';
import { debounceTime, distinctUntilChanged, finalize, map, Observable } from 'rxjs';

import { DEFAULT_NRC_CITIZEN_STATUS, DEFAULT_NRC_REGION_NO_LIST } from 'src/app/enumeration/nrc';
import { UserOccupations } from 'src/app/enumeration/occupations';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { DEFAULT_STATE_LIST } from 'src/app/enumeration/state-list';
import { CustomerApiService } from 'src/app/service/customer/customer-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { ThemeService } from 'src/app/service/theme/theme.service';

export interface PaymentProviderInfo {
  paymentProvider: string;
  paymentPhone: string;
  [key: string]: any;
}

export interface CustomerDataPayload {
  srNo: number
  loginPhone: string
  enabled: boolean
  name: string
  profile: string
  viberPhone: string
  email: string
  occupation: string
  nrc: string
  companyRegisterNo: string
  address1: string
  address2: string
  address3: string
  city: string
  township: string
  divisionState: string
  currentPoints: any
  currentPointsTs: any
  totalPoints: any
  dailyRedeemableLimit: number
  paymentProviderInfoRespLst: any[]
  nrcFrontImage: any
  nrcBackImage: any
  nrcVerifiedBy: any
  upgVerifiedBy: any
  hasIllegalClaims: boolean
  profilePhoto: any
  unverifiedReasonNote: boolean
  customerType: any
  infoRemark: any
}


@Component({
  selector: 'app-customer-info',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgbModule,
    DragDropModule,
    AngularImageViewerModule
  ],
  templateUrl: './customer-info.component.html',
  styleUrl: './customer-info.component.css',
})
export class CustomerInfoComponent implements OnInit {

  // Functional constructor-less dependency injection engine token primitives
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(CustomerApiService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly fb = inject(FormBuilder);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  // Link global layout dark state signal instantly to view parameters matrix
  readonly isDarkMode = this.themeService.isDarkMode;

  // Fully refactored component view data tracking metrics utilizing signals
  readonly customerdata = signal<CustomerDataPayload | null>(null);
  readonly userProfile = signal<SafeUrl | null>(null);
  readonly isUploading = signal<boolean>(false);
  readonly loading = signal<boolean>(false);

  // Interactive permission flags
  readonly haveAccess = signal<boolean>(false);
  readonly isNRCVerifyAccess = signal<boolean>(false);
  readonly showReasonbtn = signal<boolean>(false);
  readonly valueInfoVerified = signal<boolean>(false);
  readonly updateState = signal<boolean>(false);

  // Structural Form Architecture & Enumerations Options Data
  updateCustomerInfoForm!: FormGroup;
  login_role = UserLoginRole.SUPER_ADMIN;
  userOccupations = UserOccupations;
  stateList = DEFAULT_STATE_LIST.map((state) => state.mmName);
  stateENGList = DEFAULT_STATE_LIST.map((state) => state.engName);
  nrcStatusList = DEFAULT_NRC_CITIZEN_STATUS;
  nrcCodeList = DEFAULT_NRC_REGION_NO_LIST;

  // Sub-account datasets
  topupPhones: string[] = [];
  kbzPayAccount: any = {};
  ayaPayAccount: any = {};
  wavePayAccount: any = {};

  townShipList: any[] = [];
  nrcCityNameList: any[] = [];
  loginphone = '';
  custeomerType = 'CUSTOMER';
  infoRemark = '';
  unverifiedNote = '';
  unverifiedBy = '';
  imgSrc: any
  profilePhoto = '';
  imageIndex = 0;
  imageName = '';
  isENG = false;

  checkboxes = [
    { label: 'Profile Name', value: false },
    { label: 'Profile Photo', value: false },
    { label: 'NRC Front Photo', value: false },
    { label: 'NRC Back Photo', value: false },
    { label: 'NRC Number', value: false },
  ];

  readonly checkboxToBodyMapping: Record<string, string> = {
    'Profile Name': 'profileName',
    'Profile Photo': 'profilePhoto',
    'NRC Front Photo': 'nrcFrontPhoto',
    'NRC Back Photo': 'nrcBackPhoto',
    'NRC Number': 'nrcNumber',
  };

  // Angular Image Viewer Configuration parameters
  readonly config = {
    btnClass: 'default',
    zoomFactor: 0.1,
    containerBackgroundColor: '#e4dede',
    primaryColor: '',
    wheelZoom: true,
    allowFullscreen: true,
    allowKeyboardNavigation: true,
    isZoomPersistent: false,
    btnIcons: {
      zoomIn: 'fa fa-plus',
      zoomOut: 'fa fa-minus',
      rotateClockwise: 'fa fa-repeat',
      rotateCounterClockwise: 'fa fa-undo',
      next: 'fa fa-arrow-right',
      prev: 'fa fa-arrow-left',
      fullscreen: 'fa fa-arrows-alt',
    },
    btnShow: {
      zoomIn: true,
      zoomOut: true,
      rotateClockwise: true,
      rotateCounterClockwise: true,
      next: true,
      prev: true,
    },
  };

  constructor() {
    this.initializeFormSchema();
  }

  ngOnInit(): void {
    // Check fallback state parameters if navigating from list silently, otherwise fallback to standard paramMap snapshots
    const navigationState = this.router.getCurrentNavigation()?.extras.state || window.history.state;

    if (navigationState && navigationState['phone']) {
      this.loginphone = String(navigationState['phone']);
    } else {
      this.loginphone = String(this.route.snapshot.paramMap.get('phone'));
    }

    this.haveAccess.set(this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN]));
    this.isNRCVerifyAccess.set(this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN]));

    this.getCustomerData();
  }

  private initializeFormSchema(): void {
    this.updateCustomerInfoForm = this.fb.group({
      name: ['', Validators.required],
      profile: [''],
      viberPhone: [''],
      email: [''],
      occupation: [''],
      nrc: [''],
      companyRegisterNo: [''],
      address1: [''],
      address2: [''],
      address3: [''],
      city: [''],
      township: [''],
      divisionState: [''],
    });
  }

  /**
   * Streams custom metadata payloads matching active login phone profiles
   */
  getCustomerData(): void {
    this.loading.set(true);

    this.apiService.getCustomerInfo(this.loginphone)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v: CustomerDataPayload) => {
          this.setInitCustomerData(v);
        },
        error: (err: any) => {
          console.error('Failed to locate customer profile context nodes:', err);
          alert(err?.message || err || 'Target profile context records not found.');
          this.router.navigate(['nav/customer-list']);
        },
        complete: () => {
          this.getDownloadUserProfile();
        },
      });
  }

  private setInitCustomerData(v: CustomerDataPayload): void {
    this.customerdata.set(v);

    const providers = v?.paymentProviderInfoRespLst || [];

    this.topupPhones = providers
      .filter((p) => p.paymentProvider === 'PHONE_BILL_TOPUP')
      .map((p) => p.paymentPhone);

    this.kbzPayAccount = providers.find((p) => p.paymentProvider === 'KBZPAY') || {};
    this.ayaPayAccount = providers.find((p) => p.paymentProvider === 'AYAPAY') || {};
    this.wavePayAccount = providers.find((p) => p.paymentProvider === 'WAVEPAY') || {};

    this.showReasonbtn.set(!!v?.nrcVerifiedBy);
    this.valueInfoVerified.set(!!v?.upgVerifiedBy);
    this.updateState.set(!!v?.unverifiedReasonNote);
  }

  /**
   * Downloads user avatar attachment stream buffers down safely
   */
  getDownloadUserProfile(): void {
    this.apiService.getUserProfileDownload(this.loginphone)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: Blob) => {
          this.processUserProfileBlob(v);
        },
        error: (err: any) => {
          console.error('An exception occurred during profile buffer downloads:', err);
        }
      });
  }

  private processUserProfileBlob(data: Blob): void {
    const blob = new Blob([data], { type: 'image/jpg' });
    const url = URL.createObjectURL(blob);
    this.userProfile.set(this.sanitizer.bypassSecurityTrustUrl(url));
  }

  /**
   * Resets verification tokens across security networks channels
   */
  UnverifyNrc(): void {
    if (!confirm('Are you absolutely sure you want to revoke this customer\'s NRC verified status?')) {
      return;
    }

    this.isUploading.set(true);

    this.apiService.getUnVerifyNrc(this.loginphone)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isUploading.set(false))
      )
      .subscribe({
        next: (v: any) => {
          alert('NRC verified registration signature revoked successfully.');
          this.getCustomerData(); // Triggers global profile metrics refresh automatically
        },
        error: (err: any) => {
          console.error('Failed to clear target NRC verification tokens:', err);
          alert(err?.message || err || 'Unverify operation failed protocol checks.');
        }
      });
  }



  /**
  * Streams existing unverified infractions metadata logs down from backend endpoints
  */
  getUnVerifyReasonInfo(): void {
    this.apiService.getCustomerUnverifiedReason(this.loginphone)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: any) => {
          this.setData(v);
          // Set reactive view state signal based on length verification bounds
          this.updateState.set((v?.unverifiedBy?.length || 0) > 0);
        },
        error: (err: any) => {
          const errorMessage = err?.message || err || '';

          // FIXED: Swapped legacy .contain() with standard Javascript string .includes() to prevent runtime crashes
          if (errorMessage.includes('The customer account phone does not have unverified customer reason!')) {
            this.updateState.set(false);
          } else {
            console.error('Failed to stream unverify records metadata:', err);
            alert(errorMessage || 'Failed to pull account restriction criteria data blocks.');
          }
        }
      });
  }

  setData(data: any): void {
    if (!data) return;

    this.checkboxes = [
      { label: 'Profile Name', value: Boolean(data.profileName) },
      { label: 'Profile Photo', value: Boolean(data.profilePhoto) },
      { label: 'NRC Front Photo', value: Boolean(data.nrcFrontPhoto) },
      { label: 'NRC Back Photo', value: Boolean(data.nrcBackPhoto) },
      { label: 'NRC Number', value: Boolean(data.nrcNumber) },
    ];
    this.unverifiedNote = data.unverifiedNote || '';
    this.unverifiedBy = data.unverifiedBy || '';
  }

  /**
   * Compiles unverify audit checkboxes payload maps and transmits updates to server layers
   */
  submitUnverifyReason(): void {
    const body: Record<string, string> = {};

    this.checkboxes.forEach((checkbox) => {
      const bodyPropertyName = this.checkboxToBodyMapping[checkbox.label];
      if (bodyPropertyName) {
        body[bodyPropertyName] = this.convertBooleanToStringUpperCase(checkbox.value);
      }
    });

    body['unverifiedNote'] = this.unverifiedNote;
    body['unverifiedBy'] = this.unverifiedBy;

    this.isUploading.set(true);

    // Conditionally routes either update or create streams based on signal states values
    const apiPipeline$ = this.updateState()
      ? this.apiService.getUpdateUnverifyReason(this.loginphone, body)
      : this.apiService.getCreateUnverifyReason(this.loginphone, body);

    apiPipeline$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isUploading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Account verification profile status logs updated successfully.');
          this.closeModalViaNativeElement('showUnVerifyReason'); // Dropped jQuery dependency mapping 
          this.getUnVerifyReasonInfo(); // Re-sync entries configurations parameters mapping updates
        },
        error: (err: any) => {
          console.error('Unverify operations pipeline submit execution failure:', err);
          alert(err?.message || err || 'Failed to submit unverify reason parameters profiles maps.');
        }
      });
  }

  convertBooleanToStringUpperCase(value: boolean): string {
    return value ? 'TRUE' : 'FALSE';
  }

  /**
   * Populates the reactive modification form schema with values from the current active signal state payload
   */
  updateCustomerInfo(): void {
    // Read atomic structural data map values safely from your reactive data signal container hook pointer
    const currentData = this.customerdata();
    if (!currentData) return;

    this.updateCustomerInfoForm.patchValue({
      name: currentData.name || ' ',
      profile: currentData.profile || ' ',
      viberPhone: currentData.viberPhone || ' ',
      email: currentData.email || ' ',
      occupation: currentData.occupation || ' ',
      nrc: currentData.nrc || ' ',
      companyRegisterNo: currentData.companyRegisterNo || ' ',
      address1: currentData.address1 || ' ',
      address2: currentData.address2 || ' ',
      address3: currentData.address3 || ' ',
      city: currentData.city || ' ',
      township: currentData.township || ' ',
      divisionState: currentData.divisionState || ' ',
    });

    this.isENG = DEFAULT_STATE_LIST.some((state) => state.engName === currentData.divisionState);
    this.getTownShipList(currentData.divisionState);
  }

  onStateChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedState = selectElement.value;
    this.updateCustomerInfoForm.get('township')?.setValue(' ');
    this.getTownShipList(selectedState);
  }

  onNrcStateCodeChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selected = selectElement.value;
    this.getNRCStateCodeNameList(selected);
    this.updateNrcPart(0, selected);
  }

  onNrcTownShipChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selected = selectElement.value;
    const selectEng = selected.split('(')[0];
    this.updateNrcPart(1, selectEng);
  }

  onNrcCitizenChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selected = selectElement.value;
    this.updateNrcPart(2, selected);
  }

  onNrcNoChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const selected = inputElement.value;
    if (selected?.length > 0) {
      this.updateNrcPart(3, selected);
    }
  }

  /**
   * Vanilla DOM abstract component utility helper to close modal animations cleanly without jQuery dependencies
   */
  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }


  /**
 * Partitions the composite NRC string schema to patch isolated segments dynamically
 */
  private updateNrcPart(partIndex: number, selectedValue: string): void {
    const currentNrcValue = this.updateCustomerInfoForm.get('nrc')?.value || '';
    const extractedValues = this.extractSubstrings(currentNrcValue);

    if (extractedValues.length > 0) {
      // Update the specified index part based on selection parameters
      extractedValues[partIndex] = selectedValue;

      // Re-compile the partitioned substring array tokens back into an immutable string value
      const updatedNrc = extractedValues.join('');
      this.updateCustomerInfoForm.get('nrc')?.setValue(updatedNrc);
    }
  }

  private extractSubstrings(str: string): string[] {
    if (!str) return [];
    const substrings: string[] = [];

    // Rigorous regex boundary mapping to extract localized Myanmar NRC code blocks
    // Expected Layout matching blueprint sequence: 12/MAMANA(N)123456
    const regex = /^([^/]+\/)([^()]+)(\([^)]+\))(\d+)$/;
    const matches = str.match(regex);

    if (matches) {
      // Skip the global index 0 match capture group; map elements 1 through 4 explicitly
      substrings.push(matches[1], matches[2], matches[3], matches[4]);
    }

    return substrings;
  }

  /**
   * Queries regional infrastructure datasets to populate localized township dropdown selectors
   */
  getTownShipList(mmName: string): void {
    if (!mmName) return;
    let engName = mmName;

    if (!this.isENG) {
      engName = DEFAULT_STATE_LIST.find((state) => state.mmName === mmName)?.engName || mmName;
    }

    this.apiService.getTownShipList(engName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: any[]) => {
          this.townShipList = v || [];
        },
        error: (err: any) => {
          console.error('Failed to resolve township dataset allocations:', err);
        }
      });
  }

  /**
   * Streams verified township codes matching numerical region indexes (e.g., 12)
   */
  getNRCStateCodeNameList(code: string | number): void {
    if (!code) return;
    const parsedStateInteger = parseInt(String(code), 10);

    this.apiService.getNRCStateCodeNameList(parsedStateInteger)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: any) => {
          this.nrcCityNameList = v?.townshipCodes || [];
        },
        error: (err: any) => {
          console.error('Failed to load system state NRC codes map dictionary:', err);
        }
      });
  }

  /**
   * Functional typeahead search utility to process text stream filtering parameters
   */
  searchList = (text$: Observable<string>): Observable<string[]> => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) =>
        term.length < 1
          ? []
          : this.townShipList.filter((item: string) =>
            item.toLowerCase().startsWith(term.toLowerCase())
          )
      )
    );
  };

  /**
   * Submits modified general administrative customer profile metrics parameters over to endpoints
   */
  updateCustomerInfoBy(): void {
    if (this.updateCustomerInfoForm.invalid) return;

    this.loading.set(true);

    this.apiService.getUpdateCustomerInfoByAdmin(this.loginphone, this.updateCustomerInfoForm.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Administrative profile information updated successfully.');
          this.closeModalViaNativeElement('updateInfoModel'); // Replaced generic jQuery hook
          this.getCustomerData(); // Automatically triggers global view layout refresh signal
        },
        error: (err: any) => {
          console.error('Failed to commit profile modifications overrides:', err);
          alert(err?.message || err || 'Profiles update transmission error.');
        }
      });
  }

  /**
   * Modifies the operational transactional limits constraints profile for this account node
   */
  UpdateRedeemLimit(): void {
    const inputValue = prompt('Enter new daily redeem limit:');
    if (inputValue === null) return; // Terminate execution immediately if operator cancels prompt node

    if (!inputValue.trim() || isNaN(Number(inputValue))) {
      alert('A valid numeric integer ceiling amount constraint must be provided.');
      return;
    }

    if (!confirm(`Are you absolutely sure you want to alter the daily limitation threshold to: ${inputValue}?`)) {
      return;
    }

    this.loading.set(true);

    this.apiService.getUpdateRedeemLimitService(this.loginphone, String(inputValue))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Daily account execution limitation threshold re-allocated successfully.');
          this.getCustomerData();
        },
        error: (err: any) => {
          console.error('Failed to re-allocate redemption parameters constraints threshold:', err);
          alert(err?.message || err || 'Limitation allocation protocol failure.');
        }
      });
  }

  goToQRClaimPage(): void {
    // Preserve path navigation routing snapshots parameter dependencies mappings
    this.router.navigate(['nav/qr-code-history', this.loginphone]);
  }

  goToExChangePage(): void {
    this.router.navigate(['nav/customer-redeem-list', this.loginphone]);
  }

  goToLMSRedeemListPage(): void {
    this.router.navigate(['nav/lms-course-redeem-list'], {
      queryParams: { phone: this.loginphone }
    });
  }

  /**
   * Toggles core access tokens status profiles to restrict/restore user logins immediately
   */
  CustomerLoginEnableDisable(type: string | boolean): void {
    if (!confirm('Are you absolutely sure you want to update this customer\'s global system access clearance permissions?')) {
      return;
    }

    this.loading.set(true);

    this.apiService.getCustomerEnableDisableService(type, this.loginphone)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Customer systemic authorization profile matrix updated successfully.');
          this.getCustomerData();
        },
        error: (err: any) => {
          console.error('Failed to process authorization status toggles transmission:', err);
          alert(err?.message || err || 'Account access change failed.');
        }
      });
  }

  /**
   * Downloads legal identity documents verification binaries segments from file stores
   */
  showNrc(type: string | number): void {
    this.loading.set(true);

    this.apiService.getDownloadNrcPhoto(this.loginphone, type)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (blobBufferBytes: any) => {
          this.showFile(blobBufferBytes, type);
        },
        error: (err: any) => {
          console.error('Failed to map identity document file attachment attachments logs streams:', err);
          alert(err?.message || err || 'Requested documentation file block target not found.');
        }
      });
  }

  showFile(data: any, type: any): void {
    this.imageName = `${this.loginphone} ${type}`;

    // 🚀 The Fix: Ensure the incoming data is handled as a typed Unit8Array binary buffer
    const binaryData = data instanceof ArrayBuffer ? data : new Uint8Array(data);

    const blob = new Blob([binaryData], { type: 'image/jpeg' });
    const url = URL.createObjectURL(blob);

    // Clean up any old object URLs from memory to prevent memory leaks
    if (this.imgSrc) {
      URL.revokeObjectURL(url);
    }

    // Type safe sanitizer mapping parameters execution
    this.imgSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    console.log(this.imgSrc)
  }


  /**
   * Approves and commits verification seals onto document credentials records
   */
  verifyNrc(): void {
    this.loading.set(true);

    this.apiService.getVerifyNrc(this.loginphone)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v: any) => {
          alert('NRC validation verification tokens committed successfully.');

          // Optimistically preserve references if template logic still depends on loose attribute models mapping updates
          if (this.customerdata()) {
            this.customerdata.update(current => current ? { ...current, verifyNrc: v } : null);
          }

          this.closeModalViaNativeElement('verifyNrcConfirm');
          this.getCustomerData();
        },
        error: (err: any) => {
          console.error('Failed to commit profile validation check verification seal tokens:', err);
          alert(err?.message || err || 'Verification registry process rejected.');
        }
      });
  }

  /**
   * Records compliance profile audits metadata updates signatures across system tables
   */
  verifiedInfoBy(): void {
    this.loading.set(true);
    const targetVerificationToggleState = !this.valueInfoVerified();

    this.apiService.getCustomerInfoVerifiedBy(
      this.loginphone,
      targetVerificationToggleState,
      this.custeomerType,
      this.infoRemark
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Compliance parameter validation seals processed successfully.');
          this.valueInfoVerified.set(targetVerificationToggleState); // Mutates local widget indicators immediately
          this.closeModalViaNativeElement('showInfoVerifyDialog');
          this.getCustomerData();
        },
        error: (err: any) => {
          console.error('Failed to sign compliance parameters verification tracking logs data:', err);
          alert(err?.message || err || 'Compliance signature transaction failure.');
        }
      });
  }

  goBack(): void {
    window.history.back();
  }
}